import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Users, Send, MessageCircle, X, Minimize2, Maximize2, 
  RefreshCw, ChevronUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { SpiralIcon, HeartAlchemyIcon } from './icons/AlchemyIcons';

export default function GroupExercisePanel({ sessionId, chapter, onClose }) {
  const [userId] = useState(() => localStorage.getItem('workbook_user_id'));
  const [userName] = useState(() => localStorage.getItem('workbook_user_name') || 'Anonymous');
  
  const [session, setSession] = useState(null);
  const [inputs, setInputs] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newInput, setNewInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const lastFetchTime = useRef('');
  const pollInterval = useRef(null);

  useEffect(() => {
    if (sessionId) {
      fetchSessionData();
      // Poll for updates every 3 seconds
      pollInterval.current = setInterval(fetchUpdates, 3000);
      
      return () => {
        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }
      };
    }
  }, [sessionId]);

  const fetchSessionData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-sessions/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        setInputs(data.shared_inputs || []);
        setParticipants(data.participants || []);
        if (data.shared_inputs?.length > 0) {
          lastFetchTime.current = data.shared_inputs[data.shared_inputs.length - 1].created_at;
        }
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdates = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/group-sessions/${sessionId}/inputs${lastFetchTime.current ? `?since=${lastFetchTime.current}` : ''}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (data.inputs?.length > 0) {
          setInputs(prev => [...prev, ...data.inputs]);
          lastFetchTime.current = data.inputs[data.inputs.length - 1].created_at;
        }
        setParticipants(data.participants || []);
      }
    } catch (error) {
      console.error('Failed to fetch updates:', error);
    }
  };

  const submitInput = async () => {
    if (!newInput.trim()) return;
    if (!userId) {
      toast.error('Please log in to participate');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-sessions/${sessionId}/input`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          session_id: sessionId,
          content: newInput,
          input_type: 'text'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setInputs(prev => [...prev, data.input]);
        setNewInput('');
        lastFetchTime.current = data.input.created_at;
      }
    } catch (error) {
      toast.error('Failed to submit');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!sessionId) return null;

  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 bg-stone-900 border border-amber-500/30 rounded-lg shadow-2xl shadow-amber-900/20 transition-all duration-300 ${
        isMinimized ? 'w-64 h-12' : 'w-96 h-[500px]'
      }`}
      data-testid="group-exercise-panel"
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 border-b border-amber-500/20 cursor-pointer"
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div className="flex items-center gap-2">
          <SpiralIcon className="w-5 h-5 text-amber-400" />
          <span className="text-amber-200 font-medium text-sm">Group Session</span>
          <Badge className="bg-amber-500/20 text-amber-300 text-xs">
            {participants.length} active
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {isMinimized ? (
            <ChevronUp className="w-4 h-4 text-stone-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-stone-500" />
          )}
          <button 
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="text-stone-500 hover:text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Participants */}
          <div className="px-4 py-2 border-b border-stone-800">
            <div className="flex items-center gap-2 flex-wrap">
              <Users className="w-3 h-3 text-stone-500" />
              {participants.slice(0, 5).map((p, i) => (
                <span key={i} className="text-xs text-stone-400">
                  {p.user_name}{i < Math.min(participants.length, 5) - 1 ? ',' : ''}
                </span>
              ))}
              {participants.length > 5 && (
                <span className="text-xs text-stone-500">+{participants.length - 5} more</span>
              )}
            </div>
          </div>

          {/* Shared Inputs */}
          <ScrollArea className="h-[340px] px-4 py-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="w-6 h-6 text-amber-500 animate-spin" />
              </div>
            ) : inputs.length === 0 ? (
              <div className="text-center py-10 text-stone-500">
                <HeartAlchemyIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Share your thoughts with the group</p>
                <p className="text-xs mt-1">Everyone's inputs appear here in real-time</p>
              </div>
            ) : (
              <div className="space-y-3">
                {inputs.map((input, index) => (
                  <div 
                    key={input._id || index}
                    className={`p-3 rounded-lg ${
                      input.user_id === userId 
                        ? 'bg-amber-500/10 border border-amber-500/20 ml-4' 
                        : 'bg-stone-800/50 border border-stone-700 mr-4'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${
                        input.user_id === userId ? 'text-amber-400' : 'text-teal-400'
                      }`}>
                        {input.user_id === userId ? 'You' : input.user_name}
                      </span>
                      <span className="text-[10px] text-stone-600">
                        {formatTime(input.created_at)}
                      </span>
                    </div>
                    <p className="text-stone-300 text-sm">{input.content}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-stone-800 bg-stone-900">
            <div className="flex gap-2">
              <Input
                value={newInput}
                onChange={(e) => setNewInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && submitInput()}
                placeholder="Share your reflection..."
                className="bg-stone-800 border-stone-700 text-white text-sm"
                data-testid="group-input"
              />
              <Button
                onClick={submitInput}
                disabled={!newInput.trim()}
                className="bg-amber-600 hover:bg-amber-500 text-white px-3"
                data-testid="group-submit-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
